import type { AggregateOp } from '@ofmos/shared/field-types';
import type { AggregateRequest, GroupDimension } from '@ofmos/shared/record-group';
import type { ViewColumnAggregates } from '@ofmos/shared/view';
import { useMemo } from 'react';
import type { FieldMetadata, ObjectMetadata, ViewFilterNode } from '../../../data/client';
import type { ViewState } from '../views/viewState';
import { type SortState, type TableColumn, tableColumns, visibleColumns } from './tableModel';

/**
 * WHAT THE LIST'S CONTROLS READ AND WRITE, derived once and read from two places.
 *
 * D20 moved the view state up to `RecordListPage`; the DERIVATIONS on top of it stayed
 * inside `RecordTableView`, which was fine while the toolbar was the only thing that
 * needed them. It is not any more: below the desktop breakpoint the same four controls
 * live in the page's Adjust sheet, next to the mode switch and the duplicates action -
 * neither of which the table knows about.
 *
 * So the derivations became a hook rather than props. Both call sites compute the same
 * pure function of (object, fields, state) and cannot drift: a stored column list is
 * turned into shown columns in ONE place, and the three writers below are the only ones
 * in the product. Prop-drilling `columns`, `shownColumns`, `sort` and three callbacks
 * through the table to reach a sheet the table does not own would have been the same
 * logic with six more chances to pass the wrong one.
 */
export interface TableControls {
  readonly columns: readonly TableColumn[];
  /** The columns actually rendered: the stored list, plus the title column always. */
  readonly shownColumns: readonly TableColumn[];
  readonly sort: SortState | null;
  readonly setSort: (next: SortState | null) => void;
  readonly toggleColumn: (fieldId: string, visible: boolean) => void;
  readonly setFilter: (next: ViewFilterNode | null) => void;
  /** The grouped table's four settings (group-by-aggregation.md D15, D18, D29). */
  readonly grouping: GroupingControls;
}

/**
 * THE FOUR GROUPING SETTINGS AND THEIR WRITERS, in one member rather than eight props.
 *
 * They travel together because they are one surface - the Group control on the desktop and
 * the Group page in the phone's sheet are two PLACEMENTS of this object - and because
 * three of the four are meaningless without the first: an operation beside a group header
 * has no header to sit beside until the table is grouped.
 */
export interface GroupingControls {
  readonly groupBy: readonly GroupDimension[] | null;
  readonly groupAggregate: AggregateRequest | null;
  readonly columnAggregates: ViewColumnAggregates | null;
  readonly hideEmptyGroups: boolean;
  readonly setGroupBy: (next: readonly GroupDimension[] | null) => void;
  readonly setGroupAggregate: (next: AggregateRequest | null) => void;
  /** `null` removes the column's footer entry rather than storing an operation of none. */
  readonly setColumnAggregate: (fieldId: string, op: AggregateOp | null) => void;
  readonly setHideEmptyGroups: (next: boolean) => void;
}

export interface UseTableControlsInput {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly state: ViewState;
  readonly onStateChange: (next: (current: ViewState) => ViewState) => void;
}

export function useTableControls({
  object,
  fields,
  state,
  onStateChange,
}: UseTableControlsInput): TableControls {
  const columns = useMemo(() => tableColumns(object, fields), [object, fields]);

  /**
   * A VIEW STORES WHAT IS SHOWN; THE CONTROLS ASK WHAT IS HIDDEN.
   *
   * Empty `columnFieldIds` means "everything", which is what an unsaved list opens as
   * and what a view that never touched columns stores. Deriving the hidden set here
   * rather than storing it is what keeps a view meaningful after somebody ADDS a field:
   * a stored hidden-list would silently show the new column, a stored shown-list hides
   * it until somebody decides otherwise, which is the safer of the two.
   */
  const shownColumns =
    state.columnFieldIds.length === 0
      ? columns
      : visibleColumns(
          columns,
          columns
            .filter((column) => !state.columnFieldIds.includes(column.field.id))
            .map((column) => column.field.id),
        );

  const sort: SortState | null =
    state.orderBy === null || state.orderBy[0] === undefined
      ? null
      : { fieldId: state.orderBy[0].fieldId, direction: state.orderBy[0].direction };

  const setSort = (next: SortState | null) =>
    onStateChange((current) => ({
      ...current,
      orderBy: next === null ? null : [{ fieldId: next.fieldId, direction: next.direction }],
    }));

  const toggleColumn = (fieldId: string, visible: boolean) =>
    onStateChange((current) => {
      const shown =
        current.columnFieldIds.length === 0
          ? columns.map((column) => column.field.id)
          : current.columnFieldIds;
      const next = visible ? [...shown, fieldId] : shown.filter((id) => id !== fieldId);
      // Keep the object's own field order rather than click order, so a saved view's
      // columns read the same way the record page does.
      return {
        ...current,
        columnFieldIds: columns.map((column) => column.field.id).filter((id) => next.includes(id)),
      };
    });

  const setFilter = (next: ViewFilterNode | null) =>
    onStateChange((current) => ({ ...current, filter: next }));

  /**
   * UNGROUPING KEEPS THE REST, and that is the decision in these four lines.
   *
   * Clearing `groupBy` leaves the per-group number, the column totals and the toggle where
   * they were, so grouping by something else brings the whole arrangement back rather than
   * making the user rebuild it. Nothing is rendered from them while the table is flat - the
   * grouped read is the only thing that computes them (D22 keeps the ungrouped path exactly
   * as it was) - so what is kept is a preference, never a number on screen.
   */
  const grouping: GroupingControls = {
    groupBy: state.groupBy,
    groupAggregate: state.groupAggregate,
    columnAggregates: state.columnAggregates,
    hideEmptyGroups: state.hideEmptyGroups,
    setGroupBy: (next) => onStateChange((current) => ({ ...current, groupBy: next })),
    setGroupAggregate: (next) => onStateChange((current) => ({ ...current, groupAggregate: next })),
    setColumnAggregate: (fieldId, op) =>
      onStateChange((current) => {
        const next = { ...(current.columnAggregates ?? {}) };
        if (op === null) delete next[fieldId];
        else next[fieldId] = op;
        // No entries is no footer at all, not a `<tfoot>` with nothing in it.
        return { ...current, columnAggregates: Object.keys(next).length === 0 ? null : next };
      }),
    setHideEmptyGroups: (next) =>
      onStateChange((current) => ({ ...current, hideEmptyGroups: next })),
  };

  return { columns, shownColumns, sort, setSort, toggleColumn, setFilter, grouping };
}
